// i18n.ts
import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import pl from './translations/pl.json'
import en from './translations/en.json'
import ru from './translations/ru.json'
import ua from './translations/ua.json'
import { LOCAL_STORAGE_LANG_KEY } from '@/lib/constants/constants.ts'

const resources = {
  en: { translation: en },
  pl: { translation: pl },
  ua: { translation: ua },
  ru: { translation: ru },
}

const savedLang =
  typeof window !== 'undefined' ? localStorage.getItem(LOCAL_STORAGE_LANG_KEY) : null

// Keep <html lang> in step with the active UI language. `index.html` ships a
// static `lang="en"` while the default UI language here is Polish, so without
// this the document permanently mislabels its own content. Two consequences,
// both real: screen readers pick the wrong pronunciation rules, and Chrome
// sees declared-vs-detected language disagree, which is exactly the trigger
// for auto-translation. A translator rewrites text nodes in place underneath
// React-owned parents, and that divergence between the live DOM and the fiber
// tree is a known source of commit-phase `insertBefore` crashes.
// Our resource keys are NOT all valid BCP-47 language tags, and `lang` must be
// one: `ua` is the ISO 3166 COUNTRY code for Ukraine, whereas the ISO 639-1
// LANGUAGE code is `uk`. Writing the raw key would trade one wrong `lang` for
// another — an unrecognised tag, which assistive tech cannot act on at all.
// The app already draws this exact distinction for Intl formatting
// (`NewReportWorkspace.tsx`, `resolvedLanguage === 'ua' ? 'uk-UA' : ...`); this
// is the same mapping for the document tag, where the bare language subtag is
// enough. `pl`, `en` and `ru` are already valid and pass through unchanged.
const BCP47_BY_RESOURCE_KEY: Record<string, string> = { ua: 'uk' }

const toDocumentLang = (lng: string) => BCP47_BY_RESOURCE_KEY[lng] ?? lng

const applyDocumentLang = (lng: string) => {
  if (typeof document === 'undefined') return
  document.documentElement.lang = toDocumentLang(lng)
}

i18n.use(initReactI18next).init({
  resources,
  lng: savedLang || 'pl',
  interpolation: {
    escapeValue: false,
    // Copy that names the product must name the ACTIVE brand. On a whitelabel
    // host the deploy swaps the logo but the catalogues are compiled in, so a
    // literal "BUILD/LOG" in a sentence tells the client's own users they are
    // looking at someone else's product — the same defect as a hardcoded `alt`,
    // one layer further back. A default variable keeps it out of every call
    // site: `t()` needs no argument, and a missed one degrades to the empty
    // string rather than to our name.
    defaultVariables: { brand: __BRAND_SHORT_NAME__ },
  },
})

applyDocumentLang(i18n.language)

i18n.on('languageChanged', lng => {
  applyDocumentLang(lng)
  if (typeof window !== 'undefined') {
    localStorage.setItem(LOCAL_STORAGE_LANG_KEY, lng)
  }
})

export default i18n
